/**
 * Шейдеры для OrbLoader
 * Вынесены в отдельный файл для лучшей читаемости и возможности подсветки синтаксиса GLSL
 */

/**
 * Vertex shader для внутренней сферы орба
 */
export const orbVertexShader = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vNormal;
  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

/**
 * Fragment shader для внутренней сферы орба
 */
export const orbFragmentShader = /* glsl */ `
  uniform float iTime;
  uniform vec3 uColorAdjust; 
  varying vec2 vUv;
  varying vec3 vNormal;

  #define detail_steps_ 13
  #define mod3_ vec3(.1031, .11369, .13787)

  // --- Вспомогательные функции из вашего шейдера ---
  vec3 hash3_3(vec3 p3) {
    p3 = fract(p3 * mod3_);
    p3 += dot(p3, p3.yxz + 19.19);
    return -1. + 2. * fract(vec3((p3.x + p3.y) * p3.z, (p3.x+p3.z) * p3.y, (p3.y+p3.z) * p3.x));
  }

  float perlin_noise3(vec3 p) {
    vec3 pi = floor(p);
    vec3 pf = p - pi;
    vec3 w = pf * pf * (3. - 2. * pf);
    return mix(mix(mix(dot(pf - vec3(0,0,0), hash3_3(pi + vec3(0,0,0))), 
                       dot(pf - vec3(1,0,0), hash3_3(pi + vec3(1,0,0))), w.x),
                   mix(dot(pf - vec3(0,0,1), hash3_3(pi + vec3(0,0,1))), 
                       dot(pf - vec3(1,0,1), hash3_3(pi + vec3(1,0,1))), w.x), w.z),
               mix(mix(dot(pf - vec3(0,1,0), hash3_3(pi + vec3(0,1,0))), 
                       dot(pf - vec3(1,1,0), hash3_3(pi + vec3(1,1,0))), w.x),
                   mix(dot(pf - vec3(0,1,1), hash3_3(pi + vec3(0,1,1))), 
                       dot(pf - vec3(1,1,1), hash3_3(pi + vec3(1,1,1))), w.x), w.z), w.y);
  }

  float noise_sum_abs3(vec3 p) {
    float f = 0.; p = p * 3.;
    f += 1.0000 * abs(perlin_noise3(p)); p = 2. * p;
    f += 0.5000 * abs(perlin_noise3(p)); p = 3. * p;
    f += 0.2500 * abs(perlin_noise3(p)); p = 4. * p;
    f += 0.1250 * abs(perlin_noise3(p)); p = 5. * p;
    return f;
  }

  void main() {
    // Центрируем UV и масштабируем
    vec2 p = (vUv - 0.5) * 2.5;
    
    float electric_density = .8;
    float electric_radius  = length(p) - .5;
    float velocity = .025;
    
    float moving_coord = sin(velocity * iTime) / .2;
    vec3 electric_local_domain = vec3(p, moving_coord);
    float electric_field = electric_density * noise_sum_abs3(electric_local_domain); 
    
    float white = 0.0;
    white += (1.0 - (electric_field + electric_radius));
    
    for(int i = 0; i < detail_steps_; i++) {
      if(white >= 2.1 + float(i) / 2.) white -= .11;
    }
    
    white += 1. - 3.2 * electric_field - 0.2 * smoothstep(0.4, 0.6, electric_radius);
    white += 1. - 5.2 * electric_field - 0.4 * smoothstep(0.2, 0.8, electric_radius);
    
    // Базовый цвет шейдера (из вашего кода)
    vec3 baseCol = vec3(white - 0.5, white - 0.2, white);
    
    // --- ЭФФЕКТ ЧЕРНОЙ ПОДЛОЖКИ ---
    // Эффект Френеля: затеняем центр, оставляем свечение на краях
    float fresnel = pow(1.0 - dot(vNormal, vec3(0,0,1)), 2.0);
    
    // Смешиваем результат с цветом солнца (красным)
    vec3 finalSun = baseCol * uColorAdjust;
    
    // Умножаем на маску, чтобы в центре была "черная пустота" (подложка)
    finalSun *= (fresnel + 0.15); 

    gl_FragColor = vec4(finalSun, 1.0);
  }
`

/**
 * Vertex shader для внешней прозрачной сферы орба
 */
export const orbOuterVertexShader = /* glsl */ `
  varying vec3 vLocalPos;
  void main() {
    vLocalPos = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

/**
 * Fragment shader для внешней прозрачной сферы орба
 */
export const orbOuterFragmentShader = /* glsl */ `
  uniform float uTime;
  varying vec3 vLocalPos;

  float hash11(float p) {
    p = fract(p * .1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * .1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  mat2 rotate2d(float theta) {
    float c = cos(theta); float s = sin(theta);
    return mat2(c, -s, s, c);
  }

  float noise(vec2 p) {
    vec2 ip = floor(p); vec2 fp = fract(p);
    float a = hash12(ip); float b = hash12(ip + vec2(1, 0));
    float c = hash12(ip + vec2(0, 1)); float d = hash12(ip + vec2(1, 1));
    vec2 t = smoothstep(0.0, 1.0, fp);
    return mix(mix(a, b, t.x), mix(c, d, t.x), t.y);
  }

  float fbm(vec2 p) {
    float value = 0.0; float amplitude = 0.5;
    for (int i = 0; i < 6; i++) { // Оптимизировано до 6 октав
      value += amplitude * noise(p);
      p *= rotate2d(0.45); p *= 2.0; amplitude *= 0.5;
    }
    return value;
  }

  void main() {
    vec3 p = normalize(vLocalPos);
    
    // Превращаем 3D координаты в UV без швов (сферическая развертка)
    vec2 uv = vec2(atan(p.z, p.x) / 6.2831 + 0.5, acos(p.y) / 3.1415);
    uv = uv * 2.0 - 1.0;

    // Эффект из вашего кода
    float timeShift = 2.0 - fract(uTime / 2.0) * 4.0;
    vec2 fbmUv = uv + 0.8 * uTime;
    
    float displacement = fbm(fbmUv) * 2.0 - 1.0;
    float dist = abs(uv.x + timeShift + displacement);
    
    // Синее свечение
    vec3 col = vec3(0.2, 0.3, 0.8) * (mix(0.0, 0.09, hash11(uTime)) / dist);
    
    // Прозрачность зависит от яркости цвета
    float alpha = clamp(length(col), 0.0, 1.0);
    
    gl_FragColor = vec4(col, alpha * 0.8);
  }
`

/**
 * Noise chunk для шейдеров торнадо
 */
export const noiseChunk = /* glsl */ `
  float hash11(float p) { 
    return fract(p * .1031 * (p + 33.33)); 
  }
  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * .1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  mat2 rot(float a) { 
    return mat2(cos(a), -sin(a), sin(a), cos(a)); 
  }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    float a = hash12(i), b = hash12(i + vec2(1,0)), c = hash12(i + vec2(0,1)), d = hash12(i + vec2(1,1));
    vec2 u = f * f * (3. - 2. * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1. - u.x) + (d - b) * u.x * u.y;
  }
`

/**
 * Vertex shader для дыма торнадо
 */
export const tornadoSmokeVertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

/**
 * Fragment shader для дыма торнадо
 */
export const tornadoSmokeFragmentShader = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  ${noiseChunk}
  void main() {
    vec2 uv = vec2(vUv.x * 4.0, vUv.y * 1.5 - uTime * 0.8);
    float f = noise(uv) * 0.5 + noise(uv * 2.0 + uTime) * 0.25;
    float mask = smoothstep(0., .2, vUv.y) * smoothstep(1., .5, vUv.y);
    gl_FragColor = vec4(vec3(0.45, 0.45, 0.45) * f, f * mask * 0.3);
  }
`

/**
 * Vertex shader для молнии торнадо
 */
export const tornadoLightningVertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

/**
 * Fragment shader для молнии торнадо
 */
export const tornadoLightningFragmentShader = /* glsl */ `
  uniform float uTime;
  uniform float uFlash;
  varying vec2 vUv;
  ${noiseChunk}
  void main() {
    if (uFlash <= 0.0) discard;
    vec2 uv = vUv * 2.0 - 1.0;
    float d = abs(uv.x + (noise(vUv * 6.0 + uTime * 4.0) - 0.5) * 0.8);
    float l = pow(0.12 / (d + 0.01), 1.4);
    gl_FragColor = vec4(vec3(0.4, 0.7, 1.0) * l * uFlash, l * uFlash * smoothstep(1.0, 0.8, vUv.y));
  }
`
